import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Tag,
  Spin,
  message,
  Row,
  Col,
  Select,
  DatePicker,
  Input,
  Button,
  Tooltip,
  Image,
  Space,
  Modal,
  Typography,
  Form,
  Alert,
  Steps,
  Divider,
  Grid
} from "antd";
import {
  FilterOutlined,
  SearchOutlined,
  ReloadOutlined,
  PictureOutlined,
  ShoppingCartOutlined,
  EyeOutlined,
  KeyOutlined,
  UndoOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { getUserOrders } from "../../../services/dashboardclient/dashboardclient";
import { submitRefundRequest } from "../../../services/dashboardclient/dashboardclient";
import { formateCoins } from "../../../function";
import { useNavigate } from "react-router-dom";

const { Text, Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Step } = Steps;
const { useBreakpoint } = Grid;

// Composant Modal de Demande de Remboursement
const DemandeRemboursementModal = ({
  isOpen,
  onClose,
  order,
  onSuccess
}) => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);


  const getTranslatedErrorMessage = (serverMessage) => {
    // Mapping exhaustif des messages serveur vers les clés de traduction
    const errorMap = {
      'Seules les commandes en attente peuvent être remboursées': 'refund.onlyPendingOrders',
      'Une demande de remboursement est déjà en cours pour cet achat': 'refund.requestAlreadyExists',
      'Achat non trouvé': 'refund.orderNotFound',
      'The raison field is required.': 'refund.validationError',
      'The raison must be at least 10 characters.': 'refund.validationError',
      'The raison may not be greater than 1000 characters.': 'refund.validationError'
    };

    return errorMap[serverMessage] ? t(errorMap[serverMessage]) : serverMessage;
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const data = await submitRefundRequest(order.id, values.raison);

      if (data.success) {
        message.success(t('refund.requestSubmitted'));
        form.resetFields();
        setCurrentStep(1);
        if (onSuccess) onSuccess();
      } else {
        const errorMessage = getTranslatedErrorMessage(data.message) || t('refund.requestFailed');
        message.error(errorMessage);
      }
    } catch (error) {
      console.error('Erreur complète:', error);

      let errorMessage = t('refund.unknownError');

      if (error.response) {
        const { status, data } = error.response;

        switch (status) {
          case 400:
            errorMessage = getTranslatedErrorMessage(data.message) || t('refund.badRequest');
            break;
          case 404:
            errorMessage = t('refund.orderNotFound');
            break;
          case 422:
            errorMessage = t('refund.validationError');
            break;
          case 500:
            errorMessage = t('refund.serverError');
            break;
          default:
            errorMessage = getTranslatedErrorMessage(data.message) || t('refund.unknownError');
        }
      } else if (error.request) {
        errorMessage = t('refund.networkError');
      }

      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  const handleClose = () => {
    form.resetFields();
    setCurrentStep(0);
    onClose();
  };

  const getEligibilityStatus = () => {
    if (!order) return false;

    const isPending = order.etat === 'enattente';
    const hasPendingRequest = order.remboursement_demandes?.some(
      demande => demande.statut === 'en_attente'
    );

    return isPending && !hasPendingRequest;
  };

  const isEligible = getEligibilityStatus();

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: 'var(--primary-color3)' }} />
          <span style={{ color: 'var(--primary-color3)' }}>
            {t('RefundRequest')}
          </span>
        </Space>
      }
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={screens.xs ? "90vw" : 600}
      style={{
        maxWidth: '100vw',
        top: screens.xs ? 10 : 100
      }}
    >
      {!isEligible && (
        <Alert
          message={t('NotEligibleForRefund')}
          description={t('RefundEligibilityConditions')}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Steps
        current={currentStep}
        size="small"
        style={{ marginBottom: 24 }}
        responsive={false}
        items={[
          { title: screens.xs ? '' : t('RequestDetails') },
          { title: screens.xs ? '' : t('UnderReview') },
          // { title: screens.xs ? '' : t('Processing') }
        ]}
      />

      {currentStep === 0 && (
        <>
          <div style={{ marginBottom: 16 }}>
            {/* <Title level={5} style={{ color: 'var(--primary-color3)', fontSize: screens.xs ? '14px' : '16px' }}>
              {t('OrderDetails')}
            </Title> */}
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong style={{ color: 'var(--primary-color3)', fontSize: screens.xs ? '12px' : '14px' }}>
                {order?.offre?.titre}
              </Text>
              <Text type="secondary" style={{ color: 'var(--primary-color3)', fontSize: screens.xs ? '11px' : '12px' }}>
                {t('PurchaseDate')}: {order?.date_achat && dayjs(order.date_achat).format('DD/MM/YYYY')}
              </Text>
              <Text type="secondary" style={{ color: 'var(--primary-color3)', fontSize: screens.xs ? '11px' : '12px' }}>
                {t('Amount')}: {order?.montant} {order?.currency_payment === 'EUR' ? '€' : '$'}
              </Text>
              <Tag color="blue" style={{ fontSize: screens.xs ? '10px' : '12px' }}>
                {order?.etat}
              </Tag>
            </Space>
          </div>

          <Divider />

          {/* <Alert
            message={t('ImportantInformation')}
            description={t('RefundProcessExplanation')}
            type="info"
            showIcon
            style={{ marginBottom: 16, fontSize: screens.xs ? '12px' : '14px' }}
          /> */}

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              label={
                <Text strong style={{ color: 'var(--primary-color3)', fontSize: screens.xs ? '12px' : '14px' }}>
                  {t('RefundReason')}
                </Text>
              }
              name="raison"
              rules={[
                {
                  required: true,
                  message: t('PleaseExplainRefundReason')
                },
                {
                  min: 10,
                  message: t('ReasonMinLength')
                },
                {
                  max: 1000,
                  message: t('ReasonMaxLength')
                }
              ]}
            >
              <TextArea
                rows={screens.xs ? 3 : 4}
                placeholder={t('RefundReasonPlaceholder')}
                disabled={!isEligible}
                style={{ fontSize: screens.xs ? '12px' : '14px' }}
              />
            </Form.Item>

            <Form.Item>
              <Space direction={screens.xs ? "vertical" : "horizontal"} style={{ width: '100%' }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  disabled={!isEligible}
                  style={{ width: screens.xs ? '100%' : 'auto' }}
                >
                  {t('SubmitRequest')}
                </Button>
                <Button
                  onClick={handleClose}
                  style={{ width: screens.xs ? '100%' : 'auto' }}
                >
                  {t('Cancel')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </>
      )}

      {currentStep === 1 && (
        <div style={{ textAlign: 'center', padding: screens.xs ? '10px 0' : '20px 0' }}>
          <CheckCircleOutlined
            style={{
              fontSize: screens.xs ? 36 : 48,
              color: '#52c41a',
              marginBottom: 16
            }}
          />
          <Title level={4} style={{ color: 'var(--primary-color3)', fontSize: screens.xs ? '16px' : '18px' }}>
            {t('RequestSubmittedSuccess')}
          </Title>
          <Text style={{ color: 'var(--primary-color3)', fontSize: screens.xs ? '12px' : '14px' }}>
            {t('RequestUnderReview')}
          </Text>
          <div style={{ marginTop: 24 }}>
            <Button
              type="primary"
              onClick={handleClose}
              style={{ width: screens.xs ? '100%' : 'auto' }}
            >
              {t('Close')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

const Orders = () => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backupModalVisible, setBackupModalVisible] = useState(false);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderForRefund, setSelectedOrderForRefund] = useState(null);

  // Filtres
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserOrders();
  }, []);

  const fetchUserOrders = async (filters = {}) => {
    try {
      setLoading(true);
      const response = await getUserOrders(filters);

      if (response.success) {
        setOrders(response.data);
      } else {
        message.error(t("ErrorLoading"));
      }
    } catch (error) {
      console.error("Erreur:", error);
      message.error(t("ServerError"));
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    const filters = {
      status: statusFilter,
      startDate: dateRange && dateRange[0] ? dayjs(dateRange[0]) : null,
      endDate: dateRange && dateRange[1] ? dayjs(dateRange[1]) : null,
      search: searchTerm,
    };
    fetchUserOrders(filters);
  };

  const handleReset = () => {
    setStatusFilter("all");
    setDateRange([]);
    setSearchTerm("");
    fetchUserOrders({});
  };

  const getStatusTag = (status) => {
    const tagConfig = {
      "confirme": { color: "green", text: t("Confirmed") },
      "enattente": { color: "orange", text: t("Pending") },
      "annule": { color: "red", text: t("Cancelled") },
      "rembourse": { color: "purple", text: t("Refunded") }
    };

    const config = tagConfig[status] || { color: "default", text: t("Unknown") };

    return (
      <Tag
        color={config.color}
        style={{
          fontSize: screens.xs ? '10px' : '12px',
          margin: screens.xs ? '2px' : '4px',
          padding: screens.xs ? '2px 4px' : '4px 8px'
        }}
      >
        {config.text}
      </Tag>
    );
  };

  const formatDate = (dateString) => {
    return dayjs(dateString).format(screens.xs ? "DD/MM" : "DD/MM/YYYY");
  };

  const showBackupCodes = (order) => {
    setSelectedOrder(order);
    setBackupModalVisible(true);
  };

  const showImages = (order) => {
    setSelectedOrder(order);
    setImageModalVisible(true);
  };

  const showRefundModal = (order) => {
    // Vérifications plus complètes
    const isPending = order.etat === 'enattente';
    const hasPendingRequest = order.remboursement_demandes?.some(
      demande => demande.statut === 'en_attente'
    );
    const hasApprovedRequest = order.remboursement_demandes?.some(
      demande => demande.statut === 'approuve'
    );
    const hasRefusedRequest = order.remboursement_demandes?.some(
      demande => demande.statut === 'refuse'
    );

    if (!isPending) {
      message.warning(t('OnlyPendingOrdersCanBeRefunded'));
      return;
    }

    if (hasPendingRequest) {
      message.warning(t('PendingRefundRequestExists'));
      return;
    }

    if (hasApprovedRequest) {
      message.warning(t('RefundAlreadyApproved'));
      return;
    }

    if (hasRefusedRequest) {
      message.warning(t('RefundAlreadyRefused'));
      return;
    }

    setSelectedOrderForRefund(order);
    setRefundModalVisible(true);
  };


  // Fonctions utilitaires pour gérer les statuts de remboursement
  const getLatestRefundRequest = (demandes) => {
    if (!demandes || demandes.length === 0) return null;
    return demandes[demandes.length - 1];
  };

  const getRefundStatusTag = (demandes) => {
    const latestRequest = getLatestRefundRequest(demandes);
    if (!latestRequest) return null;

    const statusConfig = {
      'en_attente': { color: 'orange', icon: <ClockCircleOutlined />, text: t('RefundPending') },
      'approuve': { color: 'green', icon: <CheckCircleOutlined />, text: t('RefundApproved') },
      'refuse': { color: 'red', icon: <CloseCircleOutlined />, text: t('RefundRefused') }
    };

    const config = statusConfig[latestRequest.statut] || { color: 'default', icon: null, text: t('UnknownStatus') };

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const getRefundStatusIcon = (demandes) => {
    const latestRequest = getLatestRefundRequest(demandes);
    if (!latestRequest) return null;

    const iconConfig = {
      'en_attente': { icon: <ClockCircleOutlined />, color: '#fa8c16' },
      'approuve': { icon: <CheckCircleOutlined />, color: '#52c41a' },
      'refuse': { icon: <CloseCircleOutlined />, color: '#ff4d4f' }
    };

    const config = iconConfig[latestRequest.statut] || { icon: null, color: '#d9d9d9' };

    return React.cloneElement(config.icon, {
      style: { color: config.color, fontSize: '16px' }
    });
  };

  const getRefundStatusTooltip = (demandes) => {
    const latestRequest = getLatestRefundRequest(demandes);
    if (!latestRequest) return '';

    const tooltipConfig = {
      'en_attente': t('RefundRequestPendingTooltip'),
      'approuve': t('RefundApprovedTooltip'),
      'refuse': t('RefundRefusedTooltip')
    };

    return tooltipConfig[latestRequest.statut] || t('UnknownStatus');
  };

  // Colonnes responsive pour le tableau
  const getColumns = () => {
    const baseColumns = [
      {
        title: t("Offer"),
        dataIndex: ["offre", "titre"],
        key: "offre",
        render: (text, record) => (


          <Text
            style={{
              fontSize: screens.xs ? '12px' : '14px',
              fontWeight: screens.xs ? 'bold' : 'normal'
            }}
          >
            {record.offre ? formateCoins(record.offre.coins) : t("NoTitle")}
          </Text>

        ),
      },

      {
        title: t("Complete Purchase"),
        key: "complete_purchase",
        width: screens.xs ? 80 : 120,
        render: (_, record) => {
          const hasCredentials = record.login && record.password;
          const canCompletePurchase = !hasCredentials;

          if (!canCompletePurchase) {
            return (
              <Tag color="green" style={{ fontSize: screens.xs ? '9px' : '11px' }}>
                {t("Completed")}
              </Tag>
            );
          }

          return (
            <Tooltip title={t("Complete your purchase by providing account details")}>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => navigate(`/complete-purchase/${record.id}`)}
                size="small"
                style={{
                  fontSize: screens.xs ? '10px' : '12px',
                  width: screens.xs ? '100%' : 'auto'
                }}
              >
                {screens.xs ? '' : t("Complete")}
              </Button>
            </Tooltip>
          );
        },
      },

      {
        title: t("PurchaseDate"),
        dataIndex: "date_achat",
        key: "date_achat",
        render: (date) => (
          <Text style={{ fontSize: screens.xs ? '11px' : '12px' }}>
            {formatDate(date)}
          </Text>
        ),
        responsive: ['md']
      },
      {
        title: t("Amount"),
        dataIndex: "montant",
        key: "montant",
        render: (val, record) => (
          <Text style={{ fontSize: screens.xs ? '11px' : '12px', fontWeight: 'bold' }}>
            {record.formatted_price ? record.formatted_price : (val ? `${val} ${record.currency_payment === 'EUR' ? '€' : '$'}` : t("NA"))}
          </Text>
        ),
        responsive: ['sm']
      },
      {
        title: t("Status"),
        dataIndex: "etat",
        key: "etat",
        render: (etat, record) => {
          return getStatusTag(etat);
        },
      },
      {
        title: t("RefundStatus"),
        dataIndex: "remboursement_demandes",
        key: "refund_status",
        render: (demandes, record) => {
          // Vérifier s'il y a des demandes de remboursement
          const hasRefundRequests = demandes && demandes.length > 0;
          const latestRefundRequest = hasRefundRequests
            ? demandes[demandes.length - 1]
            : null;

          if (!latestRefundRequest) {
            return (
              <Text type="secondary" style={{
                fontSize: screens.xs ? '10px' : '12px',
                color: 'var(--primary-color)'
              }}>
                {t('NoRefundRequest')}
              </Text>
            );
          }

          // Définir les couleurs basées sur le statut avec votre couleur primaire
          const getTagColor = (statut) => {
            switch (statut) {
              case 'approuve':
                return 'green';
              case 'refuse':
                return 'red';
              case 'en_attente':
                return 'var(--primary-color)'; // Utilisation de votre couleur primaire
              default:
                return 'var(--primary-color)';
            }
          };

          // Définir les icônes basées sur le statut
          const getTagIcon = (statut) => {
            switch (statut) {
              case 'approuve':
                return <CheckCircleOutlined />;
              case 'refuse':
                return <CloseCircleOutlined />;
              case 'en_attente':
                return <ClockCircleOutlined />;
              default:
                return <ClockCircleOutlined />;
            }
          };

          // Définir le texte basé sur le statut
          const getTagText = (statut) => {
            switch (statut) {
              case 'approuve':
                return t('RefundApproved');
              case 'refuse':
                return t('RefundRefused');
              case 'en_attente':
                return t('RefundPending');
              default:
                return t('RefundPending');
            }
          };

          return (
            <Tag
              color={getTagColor(latestRefundRequest.statut)}
              icon={getTagIcon(latestRefundRequest.statut)}
              style={{
                fontSize: screens.xs ? '9px' : '11px',
                // Style supplémentaire si la couleur est votre variable CSS
                ...(latestRefundRequest.statut === 'en_attente' && {
                  backgroundColor: 'var(--primary-color)',
                  borderColor: 'var(--primary-color)',
                  color: 'white'
                })
              }}
            >
              {getTagText(latestRefundRequest.statut)}
            </Tag>
          );
        },
        responsive: ['md']
      }
      // {
      //   title: t("Status"),
      //   dataIndex: "etat",
      //   key: "etat",
      //   render: (etat, record) => {
      //     // Vérifier s'il y a des demandes de remboursement
      //     const hasRefundRequests = record.remboursement_demandes && record.remboursement_demandes.length > 0;

      //     return (
      //       <Space direction="vertical" size="small">
      //         {getStatusTag(etat)}

      //         {/* Afficher le statut de la demande de remboursement si elle existe */}
      //         {hasRefundRequests && (
      //           <>
      //             {record.remboursement_demandes.map((demande, index) => (
      //               <Tag 
      //                 key={demande.id}
      //                 color={
      //                   demande.statut === 'approuve' ? 'green' :
      //                   demande.statut === 'refuse' ? 'red' :
      //                   'orange'
      //                 }
      //                 icon={
      //                   demande.statut === 'approuve' ? <CheckCircleOutlined /> :
      //                   demande.statut === 'refuse' ? <CloseCircleOutlined /> :
      //                   <ClockCircleOutlined />
      //                 }
      //                 style={{ 
      //                   fontSize: screens.xs ? '9px' : '11px',
      //                   marginTop: '2px'
      //                 }}
      //               >
      //                 {demande.statut === 'approuve' ? t('RefundApproved') :
      //                  demande.statut === 'refuse' ? t('RefundRefused') :
      //                  t('RefundPending')}
      //               </Tag>
      //             ))}
      //           </>
      //         )}
      //       </Space>
      //     );
      //   },
      // }
    ];


    //   const actionsColumn = {
    //   title: t("Actions"),
    //   key: "actions",
    //   fixed: screens.xs ? 'right' : false,
    //   width: screens.xs ? 80 : 200,
    //   render: (_, record) => {
    //     // Vérifier s'il y a des demandes de remboursement en cours
    //     const hasPendingRefundRequest = record.remboursement_demandes?.some(
    //       d => d.statut === 'en_attente'
    //     );
    //     const hasApprovedRefund = record.remboursement_demandes?.some(
    //       d => d.statut === 'approuve'
    //     );
    //     const hasRefusedRefund = record.remboursement_demandes?.some(
    //       d => d.statut === 'refuse'
    //     );

    //     const canRequestRefund = 
    //       record.etat === 'enattente' && 
    //       !hasPendingRefundRequest && 
    //       !hasApprovedRefund && 
    //       !hasRefusedRefund;

    //     if (screens.xs) {
    //       return (
    //         <Space direction="vertical" size="small">
    //           <Button
    //             type="primary"
    //             icon={<EyeOutlined />}
    //             onClick={() => showImages(record)}
    //             size="small"
    //             style={{ fontSize: '10px' }}
    //           />
    //           {record.backups && record.backups.length > 0 && (
    //             <Button
    //               type="default"
    //               icon={<KeyOutlined />}
    //               onClick={() => showBackupCodes(record)}
    //               size="small"
    //               style={{ fontSize: '10px' }}
    //             />
    //           )}

    //           {/* Bouton de demande de remboursement - seulement si éligible */}
    //           {canRequestRefund && (
    //             <Button
    //               type="dashed"
    //               icon={<UndoOutlined />}
    //               onClick={() => showRefundModal(record)}
    //               size="small"
    //               danger
    //               style={{ fontSize: '10px' }}
    //             />
    //           )}

    //           {/* Message si demande en attente */}
    //           {hasPendingRefundRequest && (
    //             <Tooltip title={t('RefundRequestPendingTooltip')}>
    //               <ClockCircleOutlined 
    //                 style={{ 
    //                   color: '#fa8c16', 
    //                   fontSize: '16px',
    //                   marginLeft: '4px'
    //                 }} 
    //               />
    //             </Tooltip>
    //           )}

    //           {/* Message si demande approuvée */}
    //           {hasApprovedRefund && (
    //             <Tooltip title={t('RefundApprovedTooltip')}>
    //               <CheckCircleOutlined 
    //                 style={{ 
    //                   color: '#52c41a', 
    //                   fontSize: '16px',
    //                   marginLeft: '4px'
    //                 }} 
    //               />
    //             </Tooltip>
    //           )}

    //           {/* Message si demande refusée */}
    //           {hasRefusedRefund && (
    //             <Tooltip title={t('RefundRefusedTooltip')}>
    //               <CloseCircleOutlined 
    //                 style={{ 
    //                   color: '#ff4d4f', 
    //                   fontSize: '16px',
    //                   marginLeft: '4px'
    //                 }} 
    //               />
    //             </Tooltip>
    //           )}
    //         </Space>
    //       );
    //     } else {
    //       return (
    //         <Space direction={screens.md ? "horizontal" : "vertical"} size="small">
    //           <Button
    //             type="primary"
    //             icon={<EyeOutlined />}
    //             onClick={() => showImages(record)}
    //             size="small"
    //           >
    //             {t("View")}
    //           </Button>
    //           {record.backups && record.backups.length > 0 && (
    //             <Button
    //               type="default"
    //               icon={<KeyOutlined />}
    //               onClick={() => showBackupCodes(record)}
    //               size="small"
    //             >
    //               {t("Codes")}
    //             </Button>
    //           )}

    //           {/* Bouton de demande de remboursement - seulement si éligible */}
    //           {canRequestRefund && (
    //             <Button
    //               type="dashed"
    //               icon={<UndoOutlined />}
    //               onClick={() => showRefundModal(record)}
    //               size="small"
    //               danger
    //             >
    //               {t("RequestRefund")}
    //             </Button>
    //           )}

    //           {/* Messages pour les demandes existantes */}
    //           {hasPendingRefundRequest && (
    //             <Tag color="orange" icon={<ClockCircleOutlined />}>
    //               {t('RefundPending')}
    //             </Tag>
    //           )}
    //           {hasApprovedRefund && (
    //             <Tag color="green" icon={<CheckCircleOutlined />}>
    //               {t('RefundApproved')}
    //             </Tag>
    //           )}
    //           {hasRefusedRefund && (
    //             <Tag color="red" icon={<CloseCircleOutlined />}>
    //               {t('RefundRefused')}
    //             </Tag>
    //           )}
    //         </Space>
    //       );
    //     }
    //   },
    // };
    const actionsColumn = {
      title: t("Actions"),
      key: "actions",
      // fixed: screens.xs ? 'right' : false,
      // width: screens.xs ? 80 : 200,
      render: (_, record) => {
        // Logique séparée pour état d'achat et demandes de remboursement
        const isOrderPending = record.etat === 'enattente';
        const hasRefundRequests = record.remboursement_demandes && record.remboursement_demandes.length > 0;

        // Le bouton est affiché seulement si:
        // - La commande est en attente (état d'achat)
        // - ET il n'y a AUCUNE demande de remboursement existante
        const canRequestRefund = isOrderPending && !hasRefundRequests;

        if (screens.xs) {
          return (
            <Space direction="" size="small">
              <Button
                type="primary"
                icon={<EyeOutlined />}
                onClick={() => showImages(record)}
                size="small"
                style={{ fontSize: '10px' }}
              />
              {record.backups && record.backups.length > 0 && (
                <Button
                  type="default"
                  icon={<KeyOutlined />}
                  onClick={() => showBackupCodes(record)}
                  size="small"
                  style={{ fontSize: '10px' }}
                />
              )}

              {/* Bouton de demande de remboursement - logique séparée */}
              {canRequestRefund && (
                <Button
                  type="dashed"
                  icon={<UndoOutlined />}
                  onClick={() => showRefundModal(record)}
                  size="small"
                  danger
                  style={{ fontSize: '10px' }}
                />
              )}

              {/* Affichage des statuts de demande existants */}
              {hasRefundRequests && (
                <Tooltip title={getRefundStatusTooltip(record.remboursement_demandes)}>
                  {getRefundStatusIcon(record.remboursement_demandes)}
                </Tooltip>
              )}
            </Space>
          );
        } else {
          return (
            <Space direction={screens.md ? "horizontal" : "vertical"} size="small">
              <Button
                type="primary"
                icon={<EyeOutlined />}
                onClick={() => showImages(record)}
                size="small"
              >
                {t("View")}
              </Button>
              {record.backups && record.backups.length > 0 && (
                <Button
                  type="default"
                  icon={<KeyOutlined />}
                  onClick={() => showBackupCodes(record)}
                  size="small"
                >
                  {t("Codes")}
                </Button>
              )}

              {/* Bouton de demande de remboursement - logique séparée */}
              {canRequestRefund && (
                <Button
                  type="dashed"
                  icon={<UndoOutlined />}
                  onClick={() => showRefundModal(record)}
                  size="small"
                  danger
                >
                  {t("RequestRefund")}
                </Button>
              )}

              {/* Affichage des statuts de demande existants */}
              {hasRefundRequests && getRefundStatusTag(record.remboursement_demandes)}
            </Space>
          );
        }
      },
    };
    return [...baseColumns, actionsColumn];
  };

  return (
    <Card
      title={
        <Space>
          <ShoppingCartOutlined style={{ color: "var(--primary-color) !important" }} />
          <span style={{
            color: "var(--primary-color)",
            fontSize: screens.xs ? '16px' : '18px'
          }}>
            {t("My Orders")}
          </span>
        </Space>
      }
      style={{
        backgroundColor: "var(--background-body)",
        color: "var(--primary-color) !important",
        border: "var(--primary-color6)",
        margin: screens.xs ? '8px' : '16px'
      }}
      bodyStyle={{
        padding: screens.xs ? '12px' : '16px'
      }}
    >
      {/* Filtres Responsive */}
      <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8}>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder={t("Status")}
            style={{ width: "100%" }}
            size={screens.xs ? "small" : "middle"}
            suffixIcon={<FilterOutlined style={{ color: "var(--primary-color) !important" }} />}
          >
            <Option value="all">{t("AllStatuses")}</Option>
            <Option value="confirme">{t("Confirmed")}</Option>
            <Option value="enattente">{t("Pending")}</Option>
            <Option value="annule">{t("Cancelled")}</Option>
            <Option value="rembourse">{t("Refunded")}</Option>
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            style={{ width: "100%" }}
            size={screens.xs ? "small" : "middle"}
            format={screens.xs ? "DD/MM" : "DD/MM/YYYY"}
          />
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Input
            placeholder={t("Search")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onPressEnter={handleFilter}
            suffix={<SearchOutlined />}
            size={screens.xs ? "small" : "middle"}
          />
        </Col>
      </Row>

      {/* Boutons Filtre/Reset */}
      <Row gutter={8} style={{ marginBottom: 16 }}>
        <Col>
          <Button
            type="primary"
            icon={<FilterOutlined />}
            onClick={handleFilter}
            size={screens.xs ? "small" : "middle"}
          >
            {screens.xs ? t("FilterShort") : t("Filter")}
          </Button>
        </Col>
        <Col>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleReset}
            size={screens.xs ? "small" : "middle"}
          >
            {screens.xs ? t("ResetShort") : t("Reset")}
          </Button>
        </Col>
      </Row>

      {/* Tableau des achats - Responsive */}
      {loading ? (
        <div style={{
          display: "flex",
          justifyContent: "center",
          padding: screens.xs ? "30px" : "50px",
          marginBottom: screens.xs ? "20px" : "30px"
        }}>
          <Spin size={screens.xs ? "default" : "large"} />
        </div>
      ) : (
        <Table
          columns={getColumns()}
          dataSource={orders}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 100 }}
        />
      )}

      {/* Modals existants... */}
      <Modal
        title={
          <Space>
            <KeyOutlined style={{ color: 'var(--primary-color3)' }} />
            <span style={{
              color: 'var(--primary-color3)',
              fontSize: screens.xs ? '14px' : '16px'
            }}>
              {t("Backup Codes")} - {selectedOrder?.offre?.titre}
            </span>
          </Space>
        }
        open={backupModalVisible}
        onCancel={() => setBackupModalVisible(false)}
        footer={[
          <Button
            key="close"
            onClick={() => setBackupModalVisible(false)}
            size={screens.xs ? "small" : "middle"}
          >
            {t("Close")}
          </Button>
        ]}
        width={screens.xs ? "90vw" : 500}
      >
        {selectedOrder?.backups && selectedOrder.backups.length > 0 ? (
          <div style={{ textAlign: 'center' }}>
            {selectedOrder.backups.map((backup, index) => (
              <div key={backup.id} style={{ margin: '10px 0' }}>
                <Text
                  copyable
                  strong
                  style={{
                    fontSize: '16px',
                    color: 'var(--primary-color3)',
                    fontFamily: 'monospace'
                  }}
                >
                  {backup.code}
                </Text>
              </div>
            ))}
          </div>
        ) : (
          <Text type="secondary" style={{ fontSize: screens.xs ? '12px' : '14px' }}>
            {t("NoBackupCodes")}
          </Text>
        )}
      </Modal>

      <Modal
        title={
          <Space>
            <PictureOutlined />
            <span style={{ fontSize: screens.xs ? '14px' : '16px' }}>
              {t("Purchase Images")}
            </span>
          </Space>
        }
        open={imageModalVisible}
        onCancel={() => setImageModalVisible(false)}
        footer={[
          <Button
            key="close"
            onClick={() => setImageModalVisible(false)}
            size={screens.xs ? "small" : "middle"}
          >
            {t("Close")}
          </Button>
        ]}
        width={screens.xs ? "95vw" : 800}
      >
        {selectedOrder?.achat_images && selectedOrder.achat_images.length > 0 ? (
          <Image.PreviewGroup>
            <Row gutter={[8, 8]}>
              {selectedOrder.achat_images.map((image) => (
                <Col xs={12} sm={8} key={image.id}>
                  <Image
                    src={`${process.env.REACT_APP_MEDIA_URL}/${image.image}`}
                    alt="Purchase proof"
                    style={{
                      width: "100%",
                      borderRadius: 8,
                      maxHeight: screens.xs ? '150px' : '200px',
                      objectFit: 'cover'
                    }}
                  />
                </Col>
              ))}
            </Row>
          </Image.PreviewGroup>
        ) : (
          <Text type="secondary" style={{ fontSize: screens.xs ? '12px' : '14px' }}>
            {t("NoImages")}
          </Text>
        )}
      </Modal>

      {/* Modal pour la demande de remboursement */}
      <DemandeRemboursementModal
        isOpen={refundModalVisible}
        onClose={() => {
          setRefundModalVisible(false);
          setSelectedOrderForRefund(null);
        }}
        order={selectedOrderForRefund}
        onSuccess={() => {
          fetchUserOrders();
        }}
      />
    </Card>
  );
};

export default Orders;